import { Box, MenuItem, Popover, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AddButton,
  PrimaryButton,
  ZoomAndLocationButton,
} from '../../../components/style/ButtonStyle';
import useResponsive from '../../../hooks/useResponsive';
import {
  MyLocationIcon,
  PlusIcon,
  SearchIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from '../../../layouts/dashboard/nav/IconButton';
import { Spinner } from '../../../components/common/Spinner';

export function AddNewFacillityButton({ map }) {
  const [open, setOpen] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const mdUp = useResponsive('up', 'md');

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpen(event.currentTarget);
  };

  return (
    <>
      <AddButton
        onClick={handleOpen}
        sx={{
          zIndex: 1,
          top: { xs: '379px', md: '118px' },
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              position: 'absolute',
              bgcolor: theme => alpha(theme.palette.grey[900], 0),
            },
          }),
        }}
      >
        <PlusIcon />
      </AddButton>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={() => setOpen(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 0,
            mt: 1.5,
            ml: 0.75,
            width: 140,
            '& .MuiMenuItem-root': {
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            const center = map.getCenter();
            const zoom = map.getZoom();
            navigate(
              `/address?lat=${center._lat}&lng=${center._lng}&zoom=${zoom}`,
            );
          }}
          sx={{ m: { xs: 0, md: 1 }, justifyContent: 'center' }}
        >
          새 시설물 등록
        </MenuItem>
        {mdUp ? (
          <MenuItem onClick={() => navigate(`/facility/bulk`)} sx={{ m: 1 }}>
            시설물 일괄 등록
          </MenuItem>
        ) : (
          <></>
        )}
      </Popover>
    </>
  );
}

export function ZoomButton({ map, onZoomChange }) {
  return (
    <Stack
      spacing={2}
      sx={{
        position: 'fixed',
        top: { xs: '253px', md: 'auto' },
        left: { xs: '20px', md: 'auto' },
        bottom: { md: '40px' },
        right: { md: '20px' },
        zIndex: 1,
      }}
    >
      <ZoomAndLocationButton
        onClick={() => {
          const zoom = map.getOptions('zoom');
          map.setOptions('zoom', zoom + 1);
          onZoomChange();
        }}
      >
        <ZoomInIcon />
      </ZoomAndLocationButton>
      <ZoomAndLocationButton
        onClick={() => {
          const zoom = map.getOptions('zoom');
          map.setOptions('zoom', zoom - 1);
          onZoomChange();
        }}
      >
        <ZoomOutIcon />
      </ZoomAndLocationButton>
      <Box />
      <GeolocationButton map={map} />
    </Stack>
  );
}

function GeolocationButton({ map }) {
  const infoWindow = useMemo(() => {
    if (map) {
      return new window.naver.maps.InfoWindow();
    }
  }, [map]);

  const [loading, setLoading] = useState(false);

  function onSuccessGeolocation(position) {
    var location = new window.naver.maps.LatLng(
      position.coords.latitude,
      position.coords.longitude,
    );

    map.setZoom(16);
    map.setCenter(location); // 얻은 좌표를 지도의 중심으로 설정합니다.

    console.log('Coordinates: ' + location.toString());
    setLoading(false);
  }

  function onErrorGeolocation() {
    var center = map.getCenter();

    infoWindow.setContent(
      '<div style="padding:20px;">' +
        '<h5 style="margin-bottom:5px;color:#f00;">Geolocation failed!</h5>' +
        'latitude: ' +
        center.lat() +
        '<br />longitude: ' +
        center.lng() +
        '</div>',
    );

    infoWindow.open(map, center);
    setLoading(false);
  }

  async function onClick() {
    if (navigator.geolocation && !loading) {
      /**
       * navigator.geolocation 은 Chrome 50 버젼 이후로 HTTP 환경에서 사용이 Deprecate 되어 HTTPS 환경에서만 사용 가능 합니다.
       * http://localhost 에서는 사용이 가능하며, 테스트 목적으로, Chrome 의 바로가기를 만들어서 아래와 같이 설정하면 접속은 가능합니다.
       * chrome.exe --unsafely-treat-insecure-origin-as-secure="http://example.com"
       */
      setLoading(true);
      await navigator.geolocation.getCurrentPosition(
        onSuccessGeolocation,
        onErrorGeolocation,
        { enableHighAccuracy: true },
      );
    } else {
      var center = map.getCenter();
      infoWindow.setContent(
        '<div style="padding:20px;"><h5 style="margin-bottom:5px;color:#f00;">Geolocation not supported</h5></div>',
      );
      infoWindow.open(map, center);
    }
  }

  return (
    <>
      <ZoomAndLocationButton onClick={onClick}>
        <MyLocationIcon />
      </ZoomAndLocationButton>
      <Spinner loading={loading} />
    </>
  );
}

export function SearchNearbyButton({ onClick }) {
  return (
    <Stack
      sx={{
        position: 'fixed',
        left: { xs: '30%', md: '63%' },
        bottom: { md: '40px' },
        top: { xs: '86px', md: 'auto' },
        zIndex: 1,
        // width: '100%',
        // alignItems: 'center',
      }}
    >
      <PrimaryButton
        startIcon={<SearchIcon />}
        sx={{ opacity: 0.9 }}
        onClick={() => onClick()}
      >
        현 지도에서 검색
      </PrimaryButton>
    </Stack>
  );
}
